import {TabsWithCategory, ChatCategory, Category} from '../interfaces/api/auth/login';

/**
 * Gets the tab ID for a specific tab_value
 * @param tabs_with_categories - Array of tabs with their categories
 * @param tabValue - The tab_value to search for (e.g., 'qa', 'clinical_case')
 * @returns The tab ID as string, or undefined if not found
 */
export const getTabIdByValue = (
  tabs_with_categories: TabsWithCategory[] | undefined,
  tabValue: string,
): string | undefined => {
  if (!tabs_with_categories || !tabValue) {
    return undefined;
  }

  const tab = tabs_with_categories.find(
    (t) => t.tab_value.toLowerCase() === tabValue.toLowerCase(),
  );

  return tab ? String(tab.id) : undefined;
};

/**
 * Extracts categories from tabs_with_categories based on tab_id or tab_value
 * @param tabs_with_categories - Array of tabs with their categories
 * @param tabIdentifier - The tab identifier (can be id as number/string or tab_value as string)
 * @returns Array of categories for the specified tab, or empty array if not found
 */
export const getCategoriesFromTabs = (
  tabs_with_categories: TabsWithCategory[] | undefined,
  tabIdentifier: string | number | undefined,
): ChatCategory[] => {
  if (!tabs_with_categories || !tabIdentifier) {
    return [];
  }

  // Convert tabIdentifier to string for comparison
  const identifier = String(tabIdentifier);

  // Find the tab by id or tab_value
  const tab = tabs_with_categories.find(
    (t) =>
      String(t.id) === identifier ||
      t.tab_value === identifier ||
      t.tab_value.toLowerCase() === identifier.toLowerCase(),
  );

  // Return the categories if found, otherwise return empty array
  if (tab && tab.categories) {
    // Convert Category[] to ChatCategory[] (they have the same structure)
    return tab.categories.map((cat: Category) => ({
      id: cat.id,
      title: cat.title,
    }));
  }

  return [];
};

/**
 * Gets all categories from all tabs (for backward compatibility)
 * @param tabs_with_categories - Array of tabs with their categories
 * @returns Array of all categories from all tabs
 */
export const getAllCategoriesFromTabs = (
  tabs_with_categories: TabsWithCategory[] | undefined,
): ChatCategory[] => {
  if (!tabs_with_categories) {
    return [];
  }

  const allCategories: ChatCategory[] = [];
  tabs_with_categories.forEach((tab) => {
    if (tab.categories) {
      tab.categories.forEach((cat: Category) => {
        allCategories.push({
          id: cat.id,
          title: cat.title,
        });
      });
    }
  });

  return allCategories;
};

